/**
 * 問診票JSON（complaint1-6）とフォームデータ（complaint1_symptoms等）の相互変換
 */

export interface QuestionnaireFormData {
  [key: string]: string | string[] | boolean;
}

/** questionnaire_json の complaint1-6 形式 */
export interface QuestionnaireJsonData {
  complaint1?: { symptoms?: string[]; other?: string };
  complaint2?: { diseases?: string[]; heart_details?: string[]; other?: string };
  complaint3?: {
    has_medication?: 'none' | 'yes';
    has_medication_book?: boolean;
    has_medication_item?: boolean;
    medication_name?: string;
  };
  complaint4?: {
    bad_feeling?: 'yes' | 'no';
    allergy?: 'yes' | 'suspected' | 'no';
    pregnancy?: 'yes' | 'suspected' | 'no';
    other?: string;
  };
  complaint5?: {
    brushing?: {
      times?: string[];
      duration?: number;
      tools?: string[];
      tools_other?: string;
    };
    smoking?: 'none' | 'yes' | 'past';
    sleep_hours?: number;
    drinks?: { has_drinks?: 'none' | 'yes'; name?: string };
    snacking?: 'none' | 'regular' | 'irregular';
  };
  complaint6?: { treatments?: string[] };
}

/** questionnaire_json からフォームデータへ */
export function questionnaireJsonToFormData(json: QuestionnaireJsonData | null | undefined): QuestionnaireFormData {
  const j = json ?? {};
  const c1 = j.complaint1 ?? {};
  const c2 = j.complaint2 ?? {};
  const c3 = j.complaint3 ?? {};
  const c4 = j.complaint4 ?? {};
  const c5 = j.complaint5 ?? {};
  const c6 = j.complaint6 ?? {};

  const form: QuestionnaireFormData = {};

  form['complaint1_symptoms'] = c1.symptoms ?? [];
  form['complaint1_other'] = c1.other ?? '';

  form['complaint2_diseases'] = c2.diseases ?? [];
  form['complaint2_heart_details'] = c2.heart_details ?? [];
  form['complaint2_other'] = c2.other ?? '';

  form['complaint3_has_medication'] = c3.has_medication ?? 'none';
  form['complaint3_has_medication_book'] = c3.has_medication_book ?? false;
  form['complaint3_has_medication_item'] = c3.has_medication_item ?? false;
  form['complaint3_medication_name'] = c3.medication_name ?? '';

  form['complaint4_bad_feeling'] = c4.bad_feeling ?? 'no';
  form['complaint4_allergy'] = c4.allergy ?? 'no';
  form['complaint4_pregnancy'] = c4.pregnancy ?? 'no';
  form['complaint4_other'] = c4.other ?? '';

  const brush = c5.brushing ?? {};
  form['complaint5_brushing_times'] = brush.times ?? [];
  form['complaint5_brushing_duration'] = String(brush.duration ?? '');
  form['complaint5_brushing_tools'] = brush.tools ?? [];
  form['complaint5_brushing_tools_other'] = brush.tools_other ?? '';
  form['complaint5_smoking'] = c5.smoking ?? 'none';
  form['complaint5_sleep_hours'] = String(c5.sleep_hours ?? '');
  const drinks = c5.drinks ?? {};
  form['complaint5_has_drinks'] = drinks.has_drinks ?? 'none';
  form['complaint5_drinks_name'] = drinks.name ?? '';
  form['complaint5_snacking'] = c5.snacking ?? 'none';

  form['complaint6_treatments'] = c6.treatments ?? [];

  return form;
}

/** フォームデータから questionnaire_json へ */
export function formDataToQuestionnaireJson(form: QuestionnaireFormData): QuestionnaireJsonData {
  return {
    complaint1: {
      symptoms: (form['complaint1_symptoms'] as string[]) ?? [],
      other: (form['complaint1_other'] as string) ?? '',
    },
    complaint2: {
      diseases: (form['complaint2_diseases'] as string[]) ?? [],
      heart_details: (form['complaint2_heart_details'] as string[]) ?? [],
      other: (form['complaint2_other'] as string) ?? '',
    },
    complaint3: {
      has_medication: (form['complaint3_has_medication'] as 'none' | 'yes') ?? 'none',
      has_medication_book: (form['complaint3_has_medication_book'] as boolean) ?? false,
      has_medication_item: (form['complaint3_has_medication_item'] as boolean) ?? false,
      medication_name: (form['complaint3_medication_name'] as string) ?? '',
    },
    complaint4: {
      bad_feeling: (form['complaint4_bad_feeling'] as 'yes' | 'no') ?? 'no',
      allergy: (form['complaint4_allergy'] as 'yes' | 'suspected' | 'no') ?? 'no',
      pregnancy: (form['complaint4_pregnancy'] as 'yes' | 'suspected' | 'no') ?? 'no',
      other: (form['complaint4_other'] as string) ?? '',
    },
    complaint5: {
      brushing: {
        times: (form['complaint5_brushing_times'] as string[]) ?? [],
        duration: parseInt(String(form['complaint5_brushing_duration'] ?? ''), 10) || undefined,
        tools: (form['complaint5_brushing_tools'] as string[]) ?? [],
        tools_other: (form['complaint5_brushing_tools_other'] as string) ?? '',
      },
      smoking: (form['complaint5_smoking'] as 'none' | 'yes' | 'past') ?? 'none',
      sleep_hours: parseInt(String(form['complaint5_sleep_hours'] ?? ''), 10) || undefined,
      drinks: {
        has_drinks: (form['complaint5_has_drinks'] as 'none' | 'yes') ?? 'none',
        name: (form['complaint5_drinks_name'] as string) ?? '',
      },
      snacking: (form['complaint5_snacking'] as 'none' | 'regular' | 'irregular') ?? 'none',
    },
    complaint6: {
      treatments: (form['complaint6_treatments'] as string[]) ?? [],
    },
  };
}
